import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { toast } from "sonner";
import { Search as SearchIcon, FileText, Sparkles, Loader2 } from "lucide-react";
import Paywall from "../components/Paywall.jsx";
import { useIsPremium } from "../lib/premium";
import analytics from "../lib/analytics";

export default function ICD10() {
  const premium = useIsPremium();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [cats, setCats] = useState([]);
  const [aiQ, setAiQ] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState(null);

  useEffect(() => { if (premium) api.get("/icd10/categories").then((r) => setCats(r.data)).catch(() => {}); }, [premium]);
  useEffect(() => {
    if (!premium) return;
    const params = { limit: 200 };
    if (q) params.q = q;
    if (cat) params.category = cat;
    api.get("/icd10", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [q, cat, premium]);

  const runAi = async (e) => {
    e.preventDefault();
    if (!aiQ.trim()) return;
    setAiLoading(true);
    setAiResults(null);
    analytics.search(aiQ.trim(), "ICD10");
    try {
      const { data } = await api.post("/icd10/ai-search", { query: aiQ });
      setAiResults(data.results || []);
      if (!data.results || data.results.length === 0) toast.info("No AI suggestions — try a more specific query");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI search failed");
    } finally {
      setAiLoading(false);
    }
  };

  if (!premium) return <Paywall feature="ICD-10 Codes + AI code finder" />;

  return (
    <div className="space-y-6" data-testid="icd10-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Coding</div>
        <div className="flex items-center justify-between flex-wrap gap-2 mt-1">
          <h1 className="font-heading text-3xl font-bold text-[#111827] flex items-center gap-2">
            <FileText className="w-7 h-7 text-[#111827]" /> ICD-10 Codes
          </h1>
          <Badge className="bg-slate-100 text-slate-700 border-0" data-testid="icd10-count">{items.length} in local catalogue</Badge>
        </div>
        <p className="text-slate-600 text-sm mt-1">SA ICD-10 codes with linked tariff codes where available.</p>
      </div>

      <Card className="border-[#0B5FFF]/20 bg-gradient-to-br from-[#EFF4FF]/40 to-white">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-[#111827]" />
            <h3 className="font-heading font-semibold text-[#111827]">AI code finder</h3>
            <Badge className="bg-[#0B5FFF] text-white border-0 text-[10px]">Claude Opus 4.5</Badge>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            Describe the condition in plain English — symptom, diagnosis, injury, cause of encounter, pregnancy event, whatever — and the AI looks up the most relevant ICD-10 codes from the <strong>full WHO catalogue</strong>. Codes new to RapidCare SA are automatically saved for the next user.
          </p>
          <form onSubmit={runAi} className="flex flex-col sm:flex-row gap-2">
            <Input
              value={aiQ}
              onChange={(e) => setAiQ(e.target.value)}
              placeholder='e.g. "post-partum haemorrhage after vaginal delivery" or "fell off a motorbike, broken femur"'
              className="h-11 bg-white flex-1"
              data-testid="icd10-ai-input"
            />
            <Button type="submit" disabled={aiLoading || !aiQ.trim()} className="bg-[#0B5FFF] hover:bg-[#0A54E0] h-11 px-5" data-testid="icd10-ai-submit">
              {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing...</> : <><Sparkles className="w-4 h-4 mr-2" /> Find codes</>}
            </Button>
          </form>
          {aiResults && (
            <div className="mt-4 space-y-2" data-testid="icd10-ai-results">
              {aiResults.length === 0 ? (
                <div className="text-sm text-slate-500">No suggestions returned — try a more specific query.</div>
              ) : (
                aiResults.map((r, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-[#111827]">{r.code}</span>
                        {r.confidence !== undefined && r.confidence !== null && (
                          <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px]">{Math.round((r.confidence || 0) * 100)}% match</Badge>
                        )}
                        {r.category && (
                          <Badge variant="outline" className="text-[10px]">{r.category}</Badge>
                        )}
                        {r.source === "ai" && (
                          <Badge className="bg-[#0B5FFF]/10 text-[#111827] border border-[#0B5FFF]/20 text-[10px]" title="Sourced via AI — not yet verified against local tariff data">
                            AI-sourced
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-[#111827] mt-0.5">{r.description}</div>
                      {r.rationale && <div className="text-xs text-slate-500 mt-1 italic">{r.rationale}</div>}
                    </div>
                    {Array.isArray(r.tariff_codes) && r.tariff_codes.length > 0 && (
                      <div className="text-xs text-slate-500">Tariff: {r.tariff_codes.map((t) => t.code).join(", ")}</div>
                    )}
                  </div>
                ))
              )}
              <div className="text-[11px] text-slate-500 mt-2">
                AI decision-support only — always verify the final code against the current WHO ICD-10 and SA tariff-mapping source before billing.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keyword or code search..." className="pl-9 h-11 bg-white" data-testid="icd10-search" />
        </div>
      </div>

      {cats.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => setCat("")} className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${!cat ? "bg-[#0B5FFF] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>All</button>
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c === cat ? "" : c)} className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${cat === c ? "bg-[#0B5FFF] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>{c}</button>
          ))}
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="font-heading w-32">Code</TableHead>
                <TableHead className="font-heading">Description</TableHead>
                <TableHead className="font-heading hidden md:table-cell">Category</TableHead>
                <TableHead className="font-heading hidden lg:table-cell">Tariff</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r, i) => (
                <TableRow key={r.code} className={i % 2 ? "bg-slate-50/50" : ""}>
                  <TableCell className="font-mono font-semibold text-[#111827]">{r.code}</TableCell>
                  <TableCell>{r.description}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-slate-600">{r.category}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-slate-600">
                    {Array.isArray(r.tariff_codes) ? r.tariff_codes.map((t) => t.code).join(", ") : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {items.length === 0 && <div className="text-center py-20 text-slate-500 text-sm">No codes found.</div>}
    </div>
  );
}
